import React, { useEffect, useState, useCallback } from "react";
import { Sparkles, Thermometer, TrendingUp, Target, Clock, RefreshCw, Loader2, Zap, AlertTriangle, MessageSquareQuote, Phone, FileText, CalendarPlus, ShieldAlert, Wand2, Send, IndianRupee, Tag } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const TEMP_COLORS = {
  cold: "bg-blue-100 text-blue-700 border-blue-300",
  warm: "bg-amber-100 text-amber-700 border-amber-300",
  hot: "bg-orange-100 text-orange-700 border-orange-300",
  very_hot: "bg-rose-100 text-rose-700 border-rose-300",
};
const SENTIMENT_COLORS = {
  positive: "text-emerald-700 bg-emerald-50",
  neutral: "text-gray-700 bg-gray-50",
  negative: "text-rose-700 bg-rose-50",
};
const URGENCY_COLORS = {
  low: "bg-gray-100 text-gray-700",
  medium: "bg-amber-100 text-amber-800",
  high: "bg-orange-100 text-orange-800",
  immediate: "bg-rose-100 text-rose-800",
};
const POLICY_COLORS = {
  auto: "bg-emerald-100 text-emerald-800 border-emerald-300",
  draft: "bg-blue-100 text-blue-800 border-blue-300",
  human: "bg-rose-100 text-rose-800 border-rose-300",
};
const ACTION_LABELS = {
  share_portfolio: "Share Portfolio",
  share_package: "Share Package",
  send_quotation: "Send Quotation",
  call_customer: "Call Customer",
  book_consultation: "Book Consultation",
  send_payment_link: "Send Payment Link",
  request_drawings: "Request Drawings",
  assign_designer: "Assign Designer",
  close_lead: "Close Lead",
  archive_lead: "Archive Lead",
  no_action: "—",
};
const POLICY_LABEL = { auto: "AI may auto-send", draft: "Draft only — agent approves", human: "Human only — never auto" };

const Bar = ({ value, color }) => (
  <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
    <div className={`h-full ${color}`} style={{ width: `${Math.max(0, Math.min(100, value || 0))}%` }} />
  </div>
);

const Metric = ({ label, value, suffix = "%", barColor, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center justify-between text-[10px] uppercase font-bold text-gray-500">
      <span>{label}</span>
      <span className="text-[#1a3c5e] text-sm">{value ?? "—"}{value !== null && value !== undefined ? suffix : ""}</span>
    </div>
    <Bar value={value} color={barColor} />
  </div>
);

const ToolButton = ({ testid, Icon, label, busy, onClick }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    disabled={busy}
    className="px-2.5 py-1 bg-white border border-purple-300 hover:bg-purple-50 disabled:opacity-50 text-[11px] font-bold text-purple-700 rounded-full inline-flex items-center gap-1"
  >
    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />} {label}
  </button>
);

function NextActionButton({ conv, action }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/ai/copilot/next-action", { conversation_id: conv.id, action });
      const d = r.data;
      if (d?.tel_link) window.open(d.tel_link, "_self");
      if (d?.url && action.startsWith("share_")) toast.success("Sent to customer");
      else toast.success(`Action executed: ${action}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };
  return (
    <button onClick={run} disabled={busy} data-testid={`cc-ai-next-execute-${action}`}
      className="bg-[#F7941D] hover:bg-[#e0861a] text-white text-[10px] font-bold px-2 py-1 rounded-full inline-flex items-center gap-1 disabled:opacity-50">
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />} Do it
    </button>
  );
}

function NextActionMini({ conv, action, label, Icon }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const params = action === "send_payment_link" ? { amount: window.prompt("Amount (₹)?", "5000") || 0 } : undefined;
      const r = await api.post("/admin/ai/copilot/next-action", { conversation_id: conv.id, action, params });
      const d = r.data;
      if (d?.tel_link) window.open(d.tel_link, "_self");
      toast.success(`${label} done`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <button onClick={run} disabled={busy} data-testid={`cc-ai-mini-${action}`}
      className="bg-white border border-gray-200 hover:bg-gray-50 text-[10px] font-semibold text-gray-700 px-2 py-1 rounded inline-flex items-center justify-center gap-1 disabled:opacity-50">
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3 text-purple-600" />} {label}
    </button>
  );
}

export default function AISalesPanel({ conv, onApplyDraft }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drafting, setDrafting] = useState(null); // style name when busy
  const [busyTool, setBusyTool] = useState(null); // 'objection' | 'package' | 'qualify' | 'followup' | 'sync'
  const [objection, setObjection] = useState(null);
  const [packages, setPackages] = useState([]);
  const [qualify, setQualify] = useState(null);
  const [quotingIdx, setQuotingIdx] = useState(null);
  const [syncedTags, setSyncedTags] = useState(null);

  // Reset when switching conversation
  useEffect(() => { setObjection(null); setPackages([]); setQualify(null); setSyncedTags(null); }, [conv?.id]);

  const runObjection = async () => {
    setBusyTool("objection");
    try {
      const r = await api.post("/admin/ai/copilot/objection-handler", { conversation_id: conv.id });
      setObjection(r.data);
      if (r.data?.objection === "none") toast.info("No objection detected in this chat");
    } catch (e) {
      if (e?.response?.status === 402) toast.error("AI budget exceeded");
      else toast.error(e?.response?.data?.detail || "Objection AI error");
    } finally { setBusyTool(null); }
  };
  const runPackage = async () => {
    setBusyTool("package");
    try {
      const r = await api.post("/admin/ai/copilot/recommend-package", { conversation_id: conv.id });
      setPackages(r.data.recommendations || []);
      if (!r.data.recommendations?.length) toast.info("AI didn't find a strong package match");
    } catch (e) {
      if (e?.response?.status === 402) toast.error("AI budget exceeded");
      else toast.error(e?.response?.data?.detail || "Package AI error");
    } finally { setBusyTool(null); }
  };
  const runQualify = async () => {
    setBusyTool("qualify");
    try {
      const r = await api.post("/admin/ai/copilot/auto-qualify", { conversation_id: conv.id, apply: true });
      setQualify(r.data);
      if (r.data.applied) toast.success("Lead CRM fields auto-filled");
      else if (Object.keys(r.data.extracted || {}).length === 0) toast.info("No new CRM signals detected");
    } catch (e) {
      if (e?.response?.status === 402) toast.error("AI budget exceeded");
      else toast.error(e?.response?.data?.detail || "Auto-qualify error");
    } finally { setBusyTool(null); }
  };
  const runFollowup = async () => {
    setBusyTool("followup");
    try {
      const r = await api.post("/admin/ai/copilot/auto-followup", { conversation_id: conv.id });
      toast.success(`Follow-up scheduled at ${String(r.data.follow_up.scheduled_at).slice(0, 16).replace("T", " ")}`);
    } catch (e) {
      if (e?.response?.status === 402) toast.error("AI budget exceeded");
      else toast.error(e?.response?.data?.detail || "Follow-up scheduling failed");
    } finally { setBusyTool(null); }
  };

  const syncMarketing = async () => {
    if (!conv?.lead_id) {
      toast.error("No CRM lead linked — link a lead first");
      return;
    }
    setBusyTool("sync");
    try {
      const r = await api.post("/admin/ai/marketing-sync", { lead_id: conv.lead_id });
      setSyncedTags(r.data.tags || []);
      toast.success(`Synced — ${(r.data.tags || []).length} marketing tags applied`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Marketing sync failed");
    } finally { setBusyTool(null); }
  };

  const sendAsQuote = async (pkg, idx) => {
    if (!pkg?.service_id) {
      toast.error("This recommendation has no linked service");
      return;
    }
    if (!conv?.lead_id) {
      toast.error("Conversation has no linked CRM lead. Link a lead before sending a quotation.");
      return;
    }
    setQuotingIdx(idx);
    try {
      const r = await api.post("/admin/ai/copilot/quote-from-package", {
        conversation_id: conv.id,
        service_id: pkg.service_id,
        title: pkg.title,
        price: pkg.price,
        notes: pkg.why || undefined,
      });
      toast.success(`Quotation sent · ₹${(r.data.amount || 0).toLocaleString("en-IN")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send quotation");
    } finally { setQuotingIdx(null); }
  };

  const load = useCallback(async (force = false) => {
    if (!conv?.id) return;
    setLoading(true);
    try {
      if (force) {
        const r = await api.post("/admin/ai/copilot/analyze", { conversation_id: conv.id, force: true });
        setState(r.data);
      } else {
        const r = await api.get(`/admin/ai/copilot/state/${conv.id}`);
        if (r.data?.status === "not_analysed_yet") {
          // Trigger first analysis
          const r2 = await api.post("/admin/ai/copilot/analyze", { conversation_id: conv.id, force: false });
          setState(r2.data);
        } else {
          setState(r.data);
        }
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "AI analysis failed";
      if (e?.response?.status === 402) toast.error("AI key budget exceeded. Top up from Profile → Universal Key.");
      else toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [conv?.id]);

  useEffect(() => { load(false); }, [load]);

  const makeDraft = async (style) => {
    setDrafting(style);
    try {
      const r = await api.post("/admin/ai/copilot/draft-reply", {
        conversation_id: conv.id, style, language: "hinglish",
      });
      if (onApplyDraft) onApplyDraft({
        draft: r.data.draft,
        style,
        ai_state_intent: state?.intent,
        ai_state_confidence: state?.confidence,
      });
      toast.success(`${style} draft inserted into composer`);
    } catch (e) {
      if (e?.response?.status === 402) toast.error("AI budget exceeded");
      else toast.error(e?.response?.data?.detail || "Draft failed");
    } finally {
      setDrafting(null);
    }
  };

  if (!state) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid="cc-ai-sales-panel">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 text-sm font-bold text-purple-700"><Sparkles className="w-4 h-4" /> AI Sales Copilot</div>
          <button onClick={() => load(true)} disabled={loading} data-testid="cc-ai-refresh" className="p-1 hover:bg-gray-100 rounded">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 text-gray-500" />}
          </button>
        </div>
        <p className="text-xs text-gray-500">Analysing conversation… The Copilot reads chat history, lead details and previous orders to recommend the next move.</p>
      </div>
    );
  }
  if (state?.status === "no_messages") {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-4 text-xs text-gray-500" data-testid="cc-ai-sales-panel">
        AI Copilot will activate once the customer sends the first message.
      </div>
    );
  }

  const decision = state.policy_decision || "draft";
  const draftStyles = [
    ["professional", "Professional"],
    ["friendly", "Friendly"],
    ["premium", "Premium"],
    ["negotiation", "Negotiation"],
    ["closing", "Closing"],
    ["follow_up", "Follow-up"],
  ];

  return (
    <div className="bg-gradient-to-br from-white to-purple-50/40 rounded-2xl border border-purple-200 p-4 space-y-3" data-testid="cc-ai-sales-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-sm font-bold text-purple-700">
          <Sparkles className="w-4 h-4" /> AI Sales Copilot
        </div>
        <div className="flex items-center gap-1">
          <span className={`text-[9px] uppercase font-bold px-2 py-0.5 rounded-full border ${POLICY_COLORS[decision]}`} title={POLICY_LABEL[decision]} data-testid="cc-ai-policy-badge">
            {decision === "auto" ? <><Zap className="inline w-2.5 h-2.5 mr-0.5" /> auto</> : (decision === "draft" ? "draft" : <><AlertTriangle className="inline w-2.5 h-2.5 mr-0.5" /> human</>)}
          </span>
          <button onClick={() => load(true)} disabled={loading} data-testid="cc-ai-refresh" className="p-1 hover:bg-white/60 rounded" title="Re-analyse">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 text-gray-500" />}
          </button>
        </div>
      </div>

      {/* Summary */}
      {state.summary && (
        <div className="bg-white border border-blue-200 rounded-lg px-3 py-2 text-xs text-gray-700 italic" data-testid="cc-ai-summary">
          {state.summary}
        </div>
      )}

      {/* Top metrics */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Metric label="Lead Score" value={state.lead_score} barColor="bg-orange-500" testid="cc-ai-lead-score" />
          <Metric label="Buying Probability" value={state.buying_probability} barColor="bg-emerald-500" testid="cc-ai-buying-prob" />
          <Metric label="Customer Confidence" value={state.customer_confidence} barColor="bg-purple-500" testid="cc-ai-cust-conf" />
        </div>
        <div className="space-y-2">
          <div data-testid="cc-ai-intent-badge">
            <div className="text-[10px] uppercase font-bold text-gray-500">Intent</div>
            <div className="text-xs font-semibold text-[#1a3c5e]">{(state.intent || "").replace(/_/g, " ")}</div>
            <div className="text-[10px] text-gray-500 mt-0.5">Confidence {Math.round((state.confidence || 0) * 100)}%</div>
          </div>
          <div className="flex items-center gap-1 flex-wrap" data-testid="cc-ai-tags">
            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-full border ${TEMP_COLORS[state.lead_temperature] || ""}`}><Thermometer className="inline w-2.5 h-2.5 mr-0.5" />{(state.lead_temperature || "").replace("_", " ")}</span>
            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-full ${URGENCY_COLORS[state.urgency] || ""}`}><Clock className="inline w-2.5 h-2.5 mr-0.5" />{state.urgency || "—"}</span>
            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-full ${SENTIMENT_COLORS[state.sentiment] || ""}`}>{state.sentiment || "—"}</span>
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-gray-500">Buying Stage</div>
            <div className="text-xs font-semibold text-[#1a3c5e] capitalize">{(state.buying_stage || "").replace("_", " ")}</div>
          </div>
        </div>
      </div>

      {/* Budget + Package */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="bg-white border border-amber-200 rounded-lg p-2" data-testid="cc-ai-budget">
          <div className="text-[10px] uppercase font-bold text-amber-700">Budget Estimate</div>
          <div className="font-bold text-[#1a3c5e]">
            {state.budget_estimate_min || state.budget_estimate_max
              ? `₹${(state.budget_estimate_min || 0).toLocaleString("en-IN")} – ₹${(state.budget_estimate_max || 0).toLocaleString("en-IN")}`
              : "Not signalled"}
          </div>
        </div>
        <div className="bg-white border border-emerald-200 rounded-lg p-2" data-testid="cc-ai-package">
          <div className="text-[10px] uppercase font-bold text-emerald-700">Recommended Package</div>
          <div className="font-bold text-[#1a3c5e]">{state.recommended_package || "—"}</div>
        </div>
      </div>

      {/* Next Action */}
      <div className="bg-white border border-purple-200 rounded-lg p-2.5" data-testid="cc-ai-next-action">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase font-bold text-purple-700"><Target className="inline w-3 h-3 mr-1" /> Next Action</div>
            <div className="font-bold text-[#1a3c5e] text-sm">{ACTION_LABELS[state.recommended_action] || "—"}</div>
            {state.recommended_followup_at && (
              <div className="text-[10px] text-gray-500 mt-0.5">Follow up by {String(state.recommended_followup_at).slice(0, 16).replace("T", " ")} UTC</div>
            )}
          </div>
          {state.recommended_action && state.recommended_action !== "no_action" && (
            <NextActionButton conv={conv} action={state.recommended_action} />
          )}
        </div>
        {/* Full one-click action toolbar */}
        <div className="grid grid-cols-2 gap-1 mt-2" data-testid="cc-ai-action-grid">
          <NextActionMini conv={conv} action="share_portfolio" label="Share Portfolio" Icon={Send} />
          <NextActionMini conv={conv} action="share_package" label="Share Package" Icon={FileText} />
          <NextActionMini conv={conv} action="call_customer" label="Call Customer" Icon={Phone} />
          <NextActionMini conv={conv} action="book_consultation" label="Book Consult" Icon={CalendarPlus} />
        </div>
      </div>

      {/* Quick AI tools row */}
      <div className="flex flex-wrap gap-1.5" data-testid="cc-ai-tools-row">
        <ToolButton testid="cc-ai-objection-btn" Icon={ShieldAlert} label="Handle Objection" busy={busyTool==='objection'} onClick={runObjection} />
        <ToolButton testid="cc-ai-package-btn" Icon={Wand2} label="Recommend Package" busy={busyTool==='package'} onClick={runPackage} />
        <ToolButton testid="cc-ai-qualify-btn" Icon={Sparkles} label="Auto-qualify Lead" busy={busyTool==='qualify'} onClick={runQualify} />
        <ToolButton testid="cc-ai-followup-btn" Icon={CalendarPlus} label="Schedule Follow-up" busy={busyTool==='followup'} onClick={runFollowup} />
        <ToolButton testid="cc-ai-marketing-sync-btn" Icon={Tag} label="Sync to Marketing" busy={busyTool==='sync'} onClick={syncMarketing} />
      </div>

      {/* Marketing sync result */}
      {syncedTags && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5 text-xs" data-testid="cc-ai-marketing-result">
          <div className="font-bold text-purple-700 mb-1 flex items-center gap-1"><Tag className="w-3 h-3" /> Marketing audience tags ({syncedTags.length})</div>
          {syncedTags.length === 0 ? (
            <div className="text-gray-500 italic">No tags applied — analyse the conversation first.</div>
          ) : (
            <div className="flex flex-wrap gap-1">
              {syncedTags.map((t) => (
                <span key={t} className="px-1.5 py-0.5 rounded-full bg-white border border-purple-200 text-[10px] font-semibold text-purple-800">{t}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Objection result */}
      {objection && objection.objection && objection.objection !== "none" && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-2.5 text-xs" data-testid="cc-ai-objection-result">
          <div className="font-bold text-rose-700 mb-1 flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Objection: {objection.objection.replace(/_/g, " ")} · {objection.success_probability}% close-prob</div>
          {objection.suggested_reply && <div className="text-gray-800"><strong>Reply:</strong> {objection.suggested_reply}</div>}
          {objection.alternative_offer && <div className="text-gray-800 mt-1"><strong>Alt offer:</strong> {objection.alternative_offer}</div>}
          {objection.closing_strategy && <div className="text-gray-800 mt-1"><strong>Strategy:</strong> {objection.closing_strategy}</div>}
          {objection.suggested_reply && (
            <button onClick={() => onApplyDraft && onApplyDraft({ draft: objection.suggested_reply, style: "objection", ai_state_intent: state.intent, ai_state_confidence: state.confidence })}
              data-testid="cc-ai-objection-insert"
              className="mt-2 text-[11px] font-bold text-rose-700 hover:underline">Insert reply in composer →</button>
          )}
        </div>
      )}

      {/* Package recommendations */}
      {packages.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-xs" data-testid="cc-ai-package-result">
          <div className="font-bold text-emerald-700 mb-1.5 flex items-center gap-1"><Wand2 className="w-3 h-3" /> Recommended Packages</div>
          <div className="space-y-1.5">
            {packages.map((p, i) => (
              <div key={i} className="bg-white rounded p-1.5 border border-emerald-100" data-testid={`cc-ai-package-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-[#1a3c5e]">{p.title}</span>
                  <span className="text-emerald-700 font-bold">₹{(p.price || 0).toLocaleString("en-IN")}</span>
                </div>
                <div className="text-[10px] text-gray-600 mt-0.5">Fit {p.fit_score}% · {p.why}</div>
                {p.service_id && (
                  <button
                    onClick={() => sendAsQuote(p, i)}
                    disabled={quotingIdx === i}
                    data-testid={`cc-ai-quote-from-package-${i}`}
                    className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 hover:text-emerald-900 disabled:opacity-50"
                  >
                    {quotingIdx === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <IndianRupee className="w-3 h-3" />}
                    Send as quotation →
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Auto-qualify result */}
      {qualify && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-xs" data-testid="cc-ai-qualify-result">
          <div className="font-bold text-blue-700 mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Auto-extracted CRM fields {qualify.applied && <span className="text-emerald-700 text-[10px]">· applied to lead</span>}</div>
          {Object.keys(qualify.extracted || {}).length === 0 ? (
            <div className="text-gray-500 italic">No new CRM signals in this chat.</div>
          ) : (
            <ul className="space-y-0.5">
              {Object.entries(qualify.extracted).map(([k, v]) => (
                <li key={k}><strong>{k.replace(/_/g, " ")}:</strong> {String(v)}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Draft styles */}
      <div data-testid="cc-ai-draft-row">
        <div className="text-[10px] uppercase font-bold text-gray-500 mb-1">AI Draft Reply — pick a style</div>
        <div className="flex flex-wrap gap-1.5">
          {draftStyles.map(([key, label]) => (
            <button
              key={key}
              onClick={() => makeDraft(key)}
              disabled={!!drafting}
              data-testid={`cc-ai-draft-${key}`}
              className="px-2.5 py-1 bg-[#1a3c5e] hover:bg-[#0f2940] text-white text-[11px] font-bold rounded-full inline-flex items-center gap-1 disabled:opacity-60"
            >
              {drafting === key ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquareQuote className="w-3 h-3" />}
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Suggested reply preview (one-click apply) */}
      {state.suggested_reply && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5" data-testid="cc-ai-suggested-reply">
          <div className="text-[10px] uppercase font-bold text-purple-700 mb-1 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> AI suggested reply
            {decision === "auto" && <span className="text-[9px] text-emerald-700 bg-emerald-100 px-1.5 rounded-full ml-1">eligible for auto-send</span>}
          </div>
          <p className="text-xs text-gray-800">{state.suggested_reply}</p>
          <button
            onClick={() => onApplyDraft && onApplyDraft({ draft: state.suggested_reply, style: "suggested", ai_state_intent: state.intent, ai_state_confidence: state.confidence })}
            data-testid="cc-ai-apply-suggested"
            className="mt-1.5 text-[11px] font-bold text-purple-700 hover:underline"
          >
            Insert in composer →
          </button>
        </div>
      )}

      <div className="text-[10px] text-gray-400">Model: {state.model} · Analysed {String(state.last_analyzed_at || "").slice(11, 16)} UTC</div>
    </div>
  );
}

export { ACTION_LABELS as AI_ACTION_LABELS, POLICY_COLORS as AI_POLICY_COLORS };
